import {
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { useUpdateSavedFilterView } from '@/lib/crm/list/hooks/useSavedFilterViews';
import { logger } from '@/lib/logger';
import type { SavedFilterView } from '@/types/savedFilterView';

export interface RenameSavedViewModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  orgId: number;
  /** The entity type, used to scope the mutation's cache invalidation key. */
  entityType: string;
  /** The view being renamed. Used to seed the input and to identify which view to PATCH. */
  view: SavedFilterView | null;
  /** Optional callback fired after a successful rename, receives the updated view. */
  onRenamed?: (view: SavedFilterView) => void;
}

export function RenameSavedViewModal({
  isOpen,
  onOpenChange,
  orgId,
  entityType,
  view,
  onRenamed,
}: RenameSavedViewModalProps) {
  const [name, setName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formId = useId();

  useEffect(() => {
    if (isOpen) {
      setName(view?.name ?? '');
      setSubmitError(null);
    }
  }, [isOpen, view?.name]);

  const { mutateAsync, isPending } = useUpdateSavedFilterView(
    orgId,
    entityType
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setSubmitError('Name is required');
        return;
      }
      if (isPending || !view) {
        return;
      }
      try {
        await mutateAsync({ viewId: view.entity_id, body: { name: trimmed } });
        onRenamed?.(view);
        onOpenChange(false);
      } catch (err) {
        logger.error(
          'Error renaming saved view',
          err instanceof Error ? err : new Error(String(err))
        );
        setSubmitError("We couldn't rename this view. Try again.");
      }
    },
    [name, isPending, view, mutateAsync, onRenamed, onOpenChange]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const isNameEmpty = !name.trim();

  const footer = useMemo(
    () => (
      <div className='flex justify-end gap-2'>
        <Button color='tertiary' onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          form={formId}
          type='submit'
          color='primary'
          isDisabled={isPending || isNameEmpty}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    ),
    [formId, handleCancel, isPending, isNameEmpty]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-md'>
        <Dialog title='Rename view' footer={footer}>
          <form
            id={formId}
            onSubmit={handleSubmit}
            className='flex flex-col gap-4 py-2'
          >
            <Input
              label='Name'
              isRequired
              value={name}
              onChange={setName}
              autoFocus
              isInvalid={!!submitError && isNameEmpty}
              hint={submitError && isNameEmpty ? submitError : undefined}
            />
            {submitError && !isNameEmpty ? (
              <p className='text-sm text-fg-error'>{submitError}</p>
            ) : null}
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
